import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth, type AdminProfile } from './store';

/**
 * Keeps the signed-in admin's role + permissions fresh from
 * `GET /admin/auth/me` — on boot, on window focus, and every few minutes.
 *
 * Without this the panel would keep whatever was captured at login for the
 * whole 12 h session, so a super admin revoking access would leave the
 * operator looking at menu items whose pages now 403. The API was always
 * authoritative; this stops the UI disagreeing with it.
 *
 * A 401 is handled by the axios interceptor (it drops the session), so other
 * failures are ignored on purpose: a transient network error must not sign
 * anyone out or blank their menu.
 */
export function useSessionSync(): void {
  const token = useAuth((s) => s.token);
  const setProfile = useAuth((s) => s.setProfile);

  const { data } = useQuery({
    queryKey: ['admin', 'auth', 'me'],
    queryFn: async () => (await api.get<{ admin: AdminProfile }>('/admin/auth/me')).data.admin,
    enabled: !!token,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (data) setProfile(data);
  }, [data, setProfile]);
}
